import { useEffect, useRef, useState } from 'react'
import { Badge } from '../primitives'
import { NodeThumb } from './NodeThumb'
import { TreeIcon } from './treeIcons'
import { MOVE_KEYS } from './visible'
import type { Move, Shown } from './visible'
import { GAP, KIND, positionsText, qty, verdict } from './words'

/**
 * The same tree as an indented list — the shape that works on a phone held in
 * one hand, and the one to read top to bottom when the question is "what is on
 * the bill" rather than "how does it fit together".
 *
 * Same nodes, same collapsed set, same selection and same keyboard model as
 * the diagram (`visible.ts`), so switching between them loses nothing.
 */

export function StructureOutline({
  shown,
  selected,
  stockKnown,
  imageFor,
  onSelect,
  onToggle,
  onMove,
}: {
  shown: Shown
  selected: string | null
  stockKnown: boolean
  imageFor: (typeId: string) => string | null
  onSelect: (key: string) => void
  onToggle: (key: string) => void
  onMove: (from: string, move: Move) => string
}): JSX.Element {
  const list = useRef<HTMLDivElement>(null)
  const [focus, setFocus] = useState<{ readonly key: string; readonly n: number } | null>(null)

  useEffect(() => {
    if (focus === null) return
    list.current?.querySelector<HTMLButtonElement>(`[data-node="${CSS.escape(focus.key)}"]`)?.focus()
  }, [focus])

  const rows: Shown[] = []
  const walk = (s: Shown): void => {
    rows.push(s)
    s.children.forEach(walk)
  }
  walk(shown)

  return (
    <div className="st-outline" ref={list} role="list">
      {rows.map((row) => {
        const n = row.node
        const v = verdict(n)
        const gap = n.leaf === null ? undefined : GAP[n.leaf]
        const open = n.children.length > 0 && row.hidden === 0
        const tone = !stockKnown || n.depth === 0 ? '' : n.short > 0 ? ' st-row--short' : n.shortBelow > 0 ? ' st-row--blocked' : ''
        return (
          <div
            key={row.key}
            role="listitem"
            className={`st-row${tone}${n.depth === 0 ? ' st-row--root' : ''}${selected === row.key ? ' st-row--selected' : ''}`}
            style={{ '--depth': n.depth } as React.CSSProperties}
          >
            {n.children.length > 0 ? (
              <button
                type="button"
                className="st-row__toggle"
                tabIndex={-1}
                aria-label={open ? `Hide what ${n.typeName} is made of` : `Show what ${n.typeName} is made of`}
                aria-expanded={open}
                onClick={() => onToggle(row.key)}
              >
                <TreeIcon name={open ? 'chevronDown' : 'chevronRight'} />
              </button>
            ) : (
              <span className="st-row__toggle st-row__toggle--none" aria-hidden="true" />
            )}
            <button
              type="button"
              className="st-row__main"
              data-node={row.key}
              aria-pressed={selected === row.key}
              aria-expanded={n.children.length > 0 ? open : undefined}
              onClick={() => onSelect(row.key)}
              onKeyDown={(e) => {
                const move = MOVE_KEYS[e.key]
                if (move === undefined) return
                e.preventDefault()
                const key = onMove(row.key, move)
                setFocus((f) => ({ key, n: (f?.n ?? 0) + 1 }))
              }}
            >
              <NodeThumb imageId={imageFor(n.typeId)} kind={n.kind} consumed={n.leaf === 'consumed'} root={n.depth === 0} size={36} />
              <span className="st-row__text">
                <span className="st-row__name">
                  {n.depth > 0 ? <span className="st-row__qty">{qty(n.perParent)} ×</span> : null}
                  <span className="st-row__label">{n.typeName}</span>
                  {n.revisionName ? <span className="st-card__rev">{n.revisionName}</span> : null}
                </span>
                <span className="st-row__sub">
                  <Badge tone={KIND[n.kind].tone}>{KIND[n.kind].label}</Badge>
                  {gap ? <Badge tone={gap.tone}>{gap.label}</Badge> : null}
                  {n.positions.length > 0 ? <span className="st-card__pos">{positionsText(n.positions)}</span> : null}
                  {n.leaf === 'consumed' ? <span className="st-card__pos">consumed</span> : null}
                </span>
              </span>
              {stockKnown && n.depth > 0 ? (
                <span className="st-row__stock">
                  <span className="st-row__counts">
                    {qty(n.onShelf)} on shelf · need {qty(n.need)}
                  </span>
                  <Badge tone={v.tone}>{v.label}</Badge>
                </span>
              ) : null}
            </button>
          </div>
        )
      })}
    </div>
  )
}
